import React from 'react';
import {
  SwipeableList,
  SwipeableListItem,
  SwipeAction,
  TrailingActions,
  LeadingActions,
  Type as ListType,
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';
import { ArrowRight, X, ChevronRight } from 'lucide-react';
import { formatCurrency } from '@/lib/fmUtils';
import { TERMINAL_STAGES, NEXT_STAGE_MAP } from '@/hooks/useFMPipeline';

const STAGE_COLORS = {
  trial_scheduled: 'bg-blue-100 text-blue-700',
  trial_done: 'bg-green-100 text-green-700',
  follow_up: 'bg-orange-100 text-orange-700',
  closed_won: 'bg-green-100 text-green-800',
  closed_lost: 'bg-red-100 text-red-700',
};

const STAGE_LABELS = {
  trial_scheduled: 'Scheduled',
  trial_done: 'Trial Done',
  follow_up: 'Follow-up',
  closed_won: 'Won',
  closed_lost: 'Lost',
};

const TYPE_COLORS = {
  fresh: 'bg-green-100 text-green-700',
  renewal: 'bg-purple-100 text-purple-700',
};

/**
 * SwipeablePipelineItem - iOS-style swipeable pipeline entry card.
 * Swipe RIGHT (trailing) = advance to next stage (green reveal).
 * Swipe LEFT (leading) = mark as lost (red reveal).
 * Tap = opens edit mode via onEdit.
 *
 * Props:
 *   entry - pipeline entry object
 *   onAdvance(entryId, targetStage) - advance handler
 *   onMarkLost(entryId) - mark lost handler
 *   onEdit(entry) - tap/edit handler
 */
export default function SwipeablePipelineItem({ entry, onAdvance, onMarkLost, onEdit }) {
  const isTerminal = TERMINAL_STAGES.includes(entry.stage);
  const nextStage = NEXT_STAGE_MAP[entry.stage];
  const stageColor = STAGE_COLORS[entry.stage] || STAGE_COLORS.trial_scheduled;
  const typeColor = TYPE_COLORS[entry.type] || TYPE_COLORS.fresh;

  // Trailing actions (swipe right = advance)
  const trailingActions = () => {
    if (isTerminal || !nextStage) return null;
    return (
      <TrailingActions>
        <SwipeAction
          onClick={() => onAdvance(entry.entry_id, nextStage)}
        >
          <div className="flex items-center justify-center px-6 h-full bg-green-500 text-white font-semibold text-sm">
            <ArrowRight className="w-5 h-5 mr-1" />
            Advance
          </div>
        </SwipeAction>
      </TrailingActions>
    );
  };

  // Leading actions (swipe left = mark lost)
  const leadingActions = () => {
    if (isTerminal) return null;
    return (
      <LeadingActions>
        <SwipeAction
          onClick={() => onMarkLost(entry.entry_id)}
          destructive={false}
        >
          <div className="flex items-center justify-center px-6 h-full bg-red-500 text-white font-semibold text-sm">
            <X className="w-5 h-5 mr-1" />
            Lost
          </div>
        </SwipeAction>
      </LeadingActions>
    );
  };

  // Format trial date
  const trialDate = entry.trial_date
    ? new Date(entry.trial_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
    : null;

  return (
    <SwipeableList type={ListType.IOS} fullSwipe={false}>
      <SwipeableListItem
        trailingActions={trailingActions()}
        leadingActions={leadingActions()}
        threshold={0.25}
      >
        <div
          className={`w-full py-3 px-4 border-b border-slate-100 bg-white active:bg-slate-50 transition-colors ${
            isTerminal ? 'opacity-70' : ''
          }`}
          onClick={() => onEdit && onEdit(entry)}
          role="button"
          tabIndex={0}
        >
          {/* Row 1: Member name + Value */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-800 truncate mr-2">
              {entry.member_name || 'Unknown'}
            </span>
            <span className="text-sm font-semibold text-slate-700 flex-shrink-0">
              {formatCurrency(entry.value || 0)}
            </span>
          </div>

          {/* Row 2: Trainer name */}
          {entry.trainer_name && (
            <p className="text-xs text-slate-500 mt-0.5">
              Trainer: {entry.trainer_name}
            </p>
          )}

          {/* Row 3: Type badge, trial date, stage badge */}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${typeColor}`}>
              {entry.type === 'fresh' ? 'Fresh' : 'Renewal'}
            </span>
            {trialDate && (
              <span className="text-[11px] text-slate-400">
                Trial: {trialDate}
              </span>
            )}
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stageColor}`}>
              {STAGE_LABELS[entry.stage] || entry.stage}
            </span>
            {!isTerminal && (
              <ChevronRight className="w-3 h-3 text-slate-300 ml-auto flex-shrink-0" />
            )}
          </div>
        </div>
      </SwipeableListItem>
    </SwipeableList>
  );
}
